import { apiClient } from "@/api/api-client.ts";

interface Identifiable {
	id?: string;
}

// Enhanced error response types
interface ApiErrorResponse {
	message: string;
	errors?: Record<string, string[]>;
}

interface ApiResponse<T> {
	data: T;
	meta?: Pagination;
}

interface ApiListResponse<T> {
	data: T[];
	meta: Pagination;
}

export interface Pagination extends Record<string, unknown> {
	current_page: number;
	from: number;
	last_page: number;
	per_page: number;
	to: number;
	total: number;
	status_count: Record<string, number> | null;
}

export interface InfiniteQueryData<T> {
	pageParams: number[];
	pages: {
		data: T;
		meta: Pagination;
	}[];
}

type BaseService<T extends Identifiable, TCreate = Omit<T, "id">, TUpdate = Partial<TCreate>> = {
	list: (params?: Record<string, unknown>) => Promise<ApiListResponse<T>>;
	findById: (id: string) => Promise<T>;
	findByIdWithInclude: (params: {
		id: string;
		include?: string;
	}) => Promise<T>;
	findByIdWithParameter: (params: {
		id: string;
		parameters?: Record<string, string | number>;
	}) => Promise<T>;
	create: (data: TCreate) => Promise<T>;
	updateById: (id: string, data: TUpdate) => Promise<T>;
	updateByIdEmail: (id: string, data: TUpdate) => Promise<T>;
	upsert: (data: TCreate | T) => Promise<T>;
	deleteById: (id: string) => Promise<void>;
};

function createBaseService<T extends Identifiable, TCreate = Omit<T, "id">, TUpdate = Partial<TCreate>>(
	baseUrl: string,
	identifier: keyof T = "id",
): BaseService<T, TCreate, TUpdate> {
	return {
		list: async (params = {}) => {
			const response = await apiClient.get<ApiListResponse<T>>(`${baseUrl}`, { params });
			return response.data;
		},

		findById: async (id: string) => {
			const response = await apiClient.get<{ data: T }>(`${baseUrl}/${id}`);

			return response.data.data;
		},

		findByIdWithInclude: async (params: { id: string; include?: string }) => {
			const response = await apiClient.get<{ data: T }>(`${baseUrl}/${params.id}`, {
				params: { include: params.include },
			});

			return response.data.data;
		},

		findByIdWithParameter: async (params: {
			id: string;
			parameters?: Record<string, string | number>;
		}) => {
			const response = await apiClient.get<{ data: T }>(`${baseUrl}/${params.id}`, {
				params: {
					...params.parameters,
				},
			});

			return response.data.data;
		},

		create: async (data: TCreate) => {
			const response = await apiClient.post<ApiResponse<T>>(baseUrl, data);
			return response.data.data;
		},

		updateById: async (id: string, data: TUpdate) => {
			const response = await apiClient.put<ApiResponse<T>>(`${baseUrl}/${id}`, data);
			return response.data.data;
		},

		deleteById: async (id: string) => {
			await apiClient.delete(`${baseUrl}/${id}`);
		},

		upsert: async (data: TCreate | T) => {
			const id = (data as T)[identifier];
			if (id) {
				const response = await apiClient.put<ApiResponse<T>>(`${baseUrl}/${id}`, data);
				return response.data.data;
			}

			const response = await apiClient.post<ApiResponse<T>>(baseUrl, data);

			return response.data.data;
		},
	};
}

export default createBaseService;
